import {Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {Offer} from '../../../model/offer/offer';
import {ProposalTile} from '../../../model/proposal-tile';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../../../store/proposal/reducers/proposal.reducer';
import {OfferStatus} from '../../../enum/offer-status';
import {UserAppState} from '../../../store/user/reducers/user.reducer';
import {selectUser} from '../../../store/user/selectors/user.selectors';
import {selectUserRoles} from '../../../store/auth/selectors/auth.selectors';
import {Roles} from '../../../enum/roles';
import {User} from 'src/app/model/user';
import {selectDetailPageOffers, selectLoading, selectPrivateNotes} from '../../../store/proposal/selectors/proposal.selectors';
import {BehaviorSubject, delay, filter, map, Observable, Subject, takeUntil} from 'rxjs';
import {ProposalStatus} from '../../../enum/proposal-status';
import {Router} from '@angular/router';
import {Route} from '../../../enum/route';
import {TranslateService} from '@ngx-translate/core';
import {PrivateNotesDialogComponent} from '../../dialogs/private-notes-dialog/private-notes-dialog.component';
import {PrivateNoteLocation} from '../../../enum/private-note-location';
import {PrivateNote} from '../../../model/offer/private-note';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {RefillOfferConfirmDialogComponent} from '../../dialogs/refill-offer-confirm-dialog/refill-offer-confirm-dialog.component';
import {OfferType} from '../../../enum/offer-type';
import {OfferOverviewComponent} from '../../offer-overview/offer-overview.component';
import {OfferVersionHistoryDialogComponent} from './offer-version-history/offer-version-history-dialog.component';

@Component({
    selector: 'app-offers-list-tab',
    templateUrl: './offers-list-tab.component.html',
    styleUrls: ['./offers-list-tab.component.scss', '../../../../styles/create-something.scss'],
    standalone: false
})
export class OffersListTabComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @Input() proposalTile: ProposalTile;
  @ViewChild(OfferOverviewComponent) offerOverviewComponent: OfferOverviewComponent;

  offers$: Observable<Offer[]> = this.proposalStore.select(selectDetailPageOffers)
    .pipe(
      filter(offers => offers != null),
      map(offers => offers
        .filter(offer =>
           (offer.creator.id == this.user.id)
          || (this.roles.includes(Roles.ROLE_BROKER)
            || this.roles.includes(Roles.ROLE_UNDERWRITER)
              || this.roles.includes(Roles.ROLE_INSURER))
        ).filter(offer => (Math.round((new Date().getTime() - offer.deletionTime.getTime())/ (3600*1000)) <= 24) || (offer.deletionTime.getFullYear() == 1970))));
  selectedOffer: Offer;
  showAcceptOffer: boolean = false;
  showRetractOffer: boolean = false;
  loading$: Observable<boolean> = this.proposalStore.select(selectLoading);
  user$ = this.userStore.select(selectUser);
  roles$ = this.userStore.select(selectUserRoles);
  private roles: Roles[];
  user: User;
  Roles = Roles;
  hasLead$: Observable<boolean> = this.offers$
    .pipe(map(offers => offers.filter(offer => offer.status == OfferStatus.ACCEPTED && offer.isLeading).length > 0));

  createExternalCoverage: boolean = false;

  ProposalStatus = ProposalStatus;

  privateNoteOverview$ = this.proposalStore.select(selectPrivateNotes).pipe(
    map(privateNotes => {
      return privateNotes?.find(pn => pn.location == PrivateNoteLocation.OFFER_OVERVIEW);
    })
  );
  privateNote: PrivateNote;

  deselectOffer = new BehaviorSubject(false);
  constructor(private readonly proposalStore: Store<ProposalAppState>,
              private readonly userStore: Store<UserAppState>,
              readonly router: Router,
              public dialog: MatDialog,
              private readonly translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.roles$.pipe(takeUntil(this.destroy$)).subscribe(roles => this.roles = roles);
    this.user$.pipe(takeUntil(this.destroy$)).subscribe(user => this.user = user);
    // the delay is the transition time defined in scss
    this.deselectOffer.pipe(delay(350), takeUntil(this.destroy$)).subscribe((value) => {
        if (value) {
          this.selectedOffer = null;
          this.deselectOffer.next(false);
        }
      }
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openModal() {
    this.showAcceptOffer = true;
  }

  openRetractionModal() {
    this.showRetractOffer = true;
  }


  showAcceptOfferButton(): boolean {
    return this.selectedOffer?.status == OfferStatus.OPEN;
  }

  showRetractOfferButton(): boolean {
    let offerStatus = this.selectedOffer?.status;
    return offerStatus == OfferStatus.OPEN || offerStatus == OfferStatus.REJECTION || offerStatus == OfferStatus.PENDING || offerStatus == OfferStatus.REVISION;
  }

  coverageBelowAllowedAmount(): boolean {
    return (this.proposalTile.coverage + this.selectedOffer?.amountCovered) <= 1;
  }

  navigateToCreateExternalCoverage() {
    this.router.navigate(['/' + Route.CREATE_EXTERNAL_COVERAGE, this.proposalTile.id])?.then();
  }

  openPrivateNoteDialog(location: PrivateNoteLocation) {
    let privateNote: PrivateNote = null;
    if (location == PrivateNoteLocation.OFFER_OVERVIEW) {
      this.privateNoteOverview$.subscribe(pn => privateNote = pn);
    } else {
      privateNote = this.privateNote;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      title: this.translate.instant('privateNotesDialog.title'),
      placeholder: this.translate.instant('privateNotesDialog.placeholder'),
      optionSave: this.translate.instant('privateNotesDialog.save'),
      optionCancel: this.translate.instant('privateNotesDialog.cancel'),
      location: location,
      note: privateNote?.note,
      id: privateNote?.id,
      offerId: privateNote?.offerId,
      proposalId: this.proposalTile.id
    };
    this.dialog.open(PrivateNotesDialogComponent, dialogConfig);
  }

  protected readonly PrivateNoteLocation = PrivateNoteLocation;

  resetStateToDefault() {
    this.selectedOffer = null;
    this.deselectOffer.next(true);
  }

  showRefillOfferButton() {
    return this.selectedOffer?.status === OfferStatus.PENDING && this.selectedOffer?.offerType !== OfferType.EXTERNAL_COVERAGE;
  }

  showEditOfferButton() {
    return (this.selectedOffer?.status === OfferStatus.OPEN || this.selectedOffer?.status === OfferStatus.REVISION) && this.selectedOffer?.offerType !== OfferType.EXTERNAL_COVERAGE;
  }

  openRefillOfferModal(selectedOffer: any): void {
    const offerNumber = selectedOffer.offerId.slice(0,8);

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      offerNumber: offerNumber,
    };

    const dialogRef = this.dialog.open(RefillOfferConfirmDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.handleRefillOffer(selectedOffer);
      }
    });
  }

  handleRefillOffer(selectedOffer: any) {
    const offerId = selectedOffer.offerId;

    this.router.navigate(['/' + Route.CREATE_OFFER, this.proposalTile.id],
      {queryParams: {basedOnOffer: offerId}}
    )?.then();
  }


  onNavigateToOffer($event: Offer) {
    this.selectedOffer = $event;
    this.offerOverviewComponent.markOfferAsRead($event);
  }

  openOfferVersionHistoryDialog(offer: Offer): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = false;
    dialogConfig.maxWidth = '95vw';
    dialogConfig.width = '900px';
    dialogConfig.data = { offerId: offer.offerId };
    this.dialog.open(OfferVersionHistoryDialogComponent, dialogConfig);
  }
}
